/**
 * Virtual infinite scroll for collection/search product grids.
 * - Loads next pages via Shopify section rendering API
 * - Replaces far-off product cards with height-matched placeholders
 * - Restores cards from cache when scrolled back into view
 */
class InfiniteScrollVirtual extends HTMLElement {
  static CACHE_LIMIT = 100;
  static MOUNT_MARGIN = '150% 0px 150% 0px';

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._loading = false;
    this._cardCache = new Map();
    this._cacheOrder = [];

    this._loadObserver = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && this._loadMore()),
      { rootMargin: '0px 0px 800px 0px', threshold: 0 }
    );

    this._virtualObserver = new IntersectionObserver(
      (entries) => entries.forEach((e) => this._handleVirtualEntry(e)),
      { rootMargin: InfiniteScrollVirtual.MOUNT_MARGIN, threshold: 0 }
    );

    this._loadObserver.observe(this);
    this._observeList();
  }

  disconnectedCallback() {
    this._loadObserver?.disconnect();
    this._virtualObserver?.disconnect();
  }

  _getTarget() {
    const selector = this.getAttribute('target');
    return selector ? document.querySelector(selector) : null;
  }

  _observeList() {
    const target = this._getTarget();
    if (!target) return;

    target.querySelectorAll('product-card, .product-card-placeholder').forEach((el) => {
      this._virtualObserver.observe(el);
    });
  }

  _handleVirtualEntry(entry) {
    const el = entry.target;

    if (entry.isIntersecting) {
      if (el.classList.contains('product-card-placeholder')) {
        this._hydratePlaceholder(el);
      }
      return;
    }

    if (el.tagName === 'PRODUCT-CARD') {
      this._dehydrateCard(el);
    }
  }

  _dehydrateCard(card) {
    if (card.dataset.virtualLocked === 'true') return;

    const handle = card.getAttribute('handle');
    if (!handle) return;

    window.stripProductCardGalleryExtras?.(card);

    const height = card.offsetHeight;
    const placeholder = document.createElement('div');
    placeholder.className = 'product-card-placeholder';
    placeholder.dataset.productHandle = handle;
    placeholder.style.minHeight = height + 'px';
    placeholder.setAttribute('aria-hidden', 'true');

    this._virtualObserver.unobserve(card);
    this._cacheCard(handle, card);
    card.replaceWith(placeholder);
    this._virtualObserver.observe(placeholder);
  }

  _cacheCard(handle, card) {
    if (this._cardCache.has(handle)) {
      this._cardCache.get(handle).remove();
      this._cacheOrder = this._cacheOrder.filter((h) => h !== handle);
    }

    while (this._cacheOrder.length >= InfiniteScrollVirtual.CACHE_LIMIT) {
      const oldest = this._cacheOrder.shift();
      const stale = this._cardCache.get(oldest);
      if (stale) {
        stale.remove();
        this._cardCache.delete(oldest);
      }
    }

    this._cardCache.set(handle, card);
    this._cacheOrder.push(handle);
  }

  _hydratePlaceholder(placeholder) {
    const handle = placeholder.dataset.productHandle;
    if (!handle) return;

    const cached = this._cardCache.get(handle);
    if (!cached) return;

    this._virtualObserver.unobserve(placeholder);
    this._cardCache.delete(handle);
    this._cacheOrder = this._cacheOrder.filter((h) => h !== handle);

    placeholder.replaceWith(cached);
    this._virtualObserver.observe(cached);

    requestAnimationFrame(() => {
      window.initProductCardSizeScroll?.(cached);
      window.initProductCardImageScroll?.(cached);
      document.dispatchEvent(new CustomEvent('Flits:AjaxCollection:CollectionUpdated', { bubbles: true }));
    });
  }

  _loadMore() {
    const nextUrl = this.getAttribute('next-page-url');
    if (!nextUrl || this._loading) return;

    this._loading = true;

    fetch(nextUrl + '&section_id=' + this.getAttribute('section-id'))
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const targetSelector = this.getAttribute('target');
        const newTarget = doc.querySelector(targetSelector);
        const target = document.querySelector(targetSelector);

        if (newTarget && target) {
          target.append(...newTarget.children);
          this._observeList();
          window.initProductCardSizeScroll?.(target);
          window.initProductCardImageScroll?.(target);
        }

        const nextScroll = doc.querySelector('infinite-scroll-virtual');
        if (nextScroll?.getAttribute('next-page-url')) {
          this.setAttribute('next-page-url', nextScroll.getAttribute('next-page-url'));
        } else {
          this._stopLoading();
        }

        document.dispatchEvent(new CustomEvent('Flits:AjaxCollection:CollectionUpdated', { bubbles: true }));
      })
      .catch(() => {})
      .finally(() => {
        this._loading = false;
      });
  }

  _stopLoading() {
    this._loadObserver.disconnect();
    this.removeAttribute('next-page-url');
  }
}

if (!window.customElements.get('infinite-scroll-virtual')) {
  window.customElements.define('infinite-scroll-virtual', InfiniteScrollVirtual);
}
